import { defineComponent as _defineComponent } from 'vue'
import { withModifiers as _withModifiers, createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, resolveComponent as _resolveComponent, normalizeClass as _normalizeClass, openBlock as _openBlock, createBlock as _createBlock, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, Fragment as _Fragment, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-914651a6"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "market-list-item__favorite" }
const _hoisted_2 = { class: "market-list-item__market" }
const _hoisted_3 = { class: "market-list-item__market-info" }
const _hoisted_4 = { class: "market-list-item__market-details" }
const _hoisted_5 = { class: "market-list-item__market-row" }
const _hoisted_6 = { class: "market-list-item__type" }
const _hoisted_7 = {
  key: 0,
  class: "market-list-item__market-volume-row"
}
const _hoisted_8 = {
  key: 1,
  class: "market-list-item__volume"
}
const _hoisted_9 = { key: 2 }
const _hoisted_10 = { class: "market-list-item__price-change-chart-wrapper" }
const _hoisted_11 = { class: "market-list-item__price-change-wrapper" }
const _hoisted_12 = {
  key: 0,
  class: "market-list-item__new-tag"
}
const _hoisted_13 = {
  key: 0,
  class: "market-list-item__price-change"
}
const _hoisted_14 = {
  key: 1,
  class: "market-list-item__new-tag"
}

import { computed, type PropType, ref, watch, nextTick } from 'vue';

import BigNumber from '@exchange/helpers/bignumber';
import useMQBreakpoints from '@exchange/libs/composables/shared/src/lib/useMQBreakpoints';
import { MarketModel, MarketState } from '@exchange/libs/market/service/src';
import { CONSTANTS } from '@exchange/libs/utils/constants/src';
import { currencyService } from '@exchange/libs/utils/currency/src';
import { themingInstance } from '@exchange/libs/utils/theming/src';

import MarketFavoriteControl from './MarketFavoriteControl.vue';
import MiniatureLineChart from './MiniatureLineChart.vue';

type MarketListClasses = {
  active?: boolean;
  suspended?: boolean;
  'in-maintenance'?: boolean;
  new?: boolean;
};

export default /*#__PURE__*/_defineComponent({
  __name: 'MarketListItem',
  props: {
  filteredBy: { type: String },
  isActive: { type: Boolean },
  isFavorite: { type: Boolean, default: false },
  isFutures: { type: Boolean, default: false },
  market: { type: Object as PropType<MarketModel>, required: true },
  withLogo: { type: Boolean, required: true },
  withPriceChangeChart: { type: Boolean, default: false },
},
  emits: ["item:favorite-clicked", "item:market-clicked"],
  setup(__props, { emit: __emit }) {

const props = __props;
const emit = __emit;

const { isXS } = useMQBreakpoints();
const { currencies } = currencyService;

const animatePriceChangingClass = ref('');

const marketUiState = computed(() => {
  if (!props.market.getMarketUiState) {
    return undefined;
  }

  return props.market.getMarketUiState();
});
const isNewMarket = computed(() => marketUiState.value === MarketState.NEW);
const fiatVolume = computed(() => {
  if (!props.market.allDay.globalVolume || !currencies.value) {
    return Number.NaN;
  }

  return new BigNumber(props.market.allDay.globalVolume).times(currencies.value[CONSTANTS.DEFAULT_FOR_CURRENCY_FORMATTING]?.defaultFiatValue ?? 0);
});

const colorVal = computed(() => {
  const getColorBasedOnPriceChange = () => {
    if (props.market.allDay.priceChange < 0) {
      return themingInstance.getColor('--v-theme-error');
    }

    if (props.market.allDay.priceChange > 0) {
      return themingInstance.getColor('--v-theme-success');
    }

    return themingInstance.getColor('--v-theme-text-error'); // same color as applied in .market-list-menu-content
  };

  if (!props.market.getMarketUiState) {
    return getColorBasedOnPriceChange();
  }

  switch (marketUiState.value) {
    case MarketState.NEW:
      return themingInstance.getColor('--v-theme-primary');
    case MarketState.SUSPENDED:
    case MarketState.IN_MAINTENANCE:
      return themingInstance.getColor('--v-theme-text-tertiary'); // font color is used to get the same color as the text
    default:
      return getColorBasedOnPriceChange();
  }
});

const classes = computed<MarketListClasses>(() => ({
  ...(!isXS.value && { active: props.isActive }),
  ...(marketUiState.value === MarketState.SUSPENDED && { suspended: true }),
  ...(marketUiState.value === MarketState.IN_MAINTENANCE && { 'in-maintenance': true }),
  ...(marketUiState.value === MarketState.NEW && { new: true }),
}));

const priceChangeClass = computed(() => {
  if (props.market.allDay.priceChangePercentage > 0) {
    return 'positive';
  }

  if (props.market.allDay.priceChangePercentage < 0) {
    return 'negative';
  }

  return '';
});

const changeMarket = () => {
  emit('item:market-clicked', props.market.id);
};

const favoriteClicked = () => {
  emit('item:favorite-clicked', props.market.id);
};

const defaultPrecision = {
  percentage: CONSTANTS.PRECISION.DEFAULT_PERCENTAGE,
  zero: CONSTANTS.PRECISION.ZERO,
};

watch(
  () => props.market?.lastPrice,
  (newPrice, oldPrice) => {
    let animateClass = '';

    if (newPrice > oldPrice) {
      animateClass = 'up';
    } else if (newPrice < oldPrice) {
      animateClass = 'down';
    }

    // clear class to force updating class
    animatePriceChangingClass.value = '';
    nextTick(() => {
      animatePriceChangingClass.value = animateClass;
    });
  },
);

return (_ctx: any,_cache: any) => {
  const _component_x_logo = _resolveComponent("x-logo")!
  const _component_ot_market_id = _resolveComponent("ot-market-id")!
  const _component_ot_lozenge = _resolveComponent("ot-lozenge")!
  const _component_ot_volume_display = _resolveComponent("ot-volume-display")!
  const _component_ot_number_display = _resolveComponent("ot-number-display")!

  return (_openBlock(), _createElementBlock("tr", {
    class: _normalizeClass(["market-list-item", classes.value]),
    onClick: changeMarket
  }, [
    _createElementVNode("td", _hoisted_1, [
      _createVNode(MarketFavoriteControl, {
        "current-market-id": __props.market.id,
        "is-favorite": __props.isFavorite,
        onClick: _withModifiers(favoriteClicked, ["stop"])
      }, null, 8, ["current-market-id", "is-favorite"])
    ]),
    _createElementVNode("td", null, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", _hoisted_3, [
          (_openBlock(), _createBlock(_component_x_logo, {
            key: __props.market.name,
            class: _normalizeClass(["market-list-item__logo", { 'market-list-item__logo--xs': _unref(isXS) }]),
            name: __props.market.base.code,
            type: "crypto"
          }, null, 8, ["class", "name"])),
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("div", _hoisted_5, [
              _createVNode(_component_ot_market_id, {
                "market-id": __props.market.name,
                "underemphasize-symbol": __props.filteredBy
              }, null, 8, ["market-id", "underemphasize-symbol"]),
              _createElementVNode("div", _hoisted_6, _toDisplayString(__props.market.type), 1),
              (__props.isFutures)
                ? (_openBlock(), _createBlock(_component_ot_lozenge, {
                    key: 0,
                    uppercase: false,
                    filled: false,
                    text: "10x",
                    class: "market-list-item__name-lozenge"
                  }))
                : _createCommentVNode("", true)
            ]),
            (_unref(isXS))
              ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                  _createVNode(_component_ot_volume_display, {
                    number: fiatVolume.value,
                    class: "market-list-item__market-volume"
                  }, null, 8, ["number"])
                ]))
              : _createCommentVNode("", true)
          ])
        ])
      ])
    ]),
    (!_unref(isXS))
      ? (_openBlock(), _createElementBlock("td", {
          key: 0,
          class: _normalizeClass(["market-list-item__price", animatePriceChangingClass.value])
        }, [
          _createVNode(_component_ot_number_display, {
            precision: __props.market.marketPrecision,
            number: __props.market.lastPrice
          }, null, 8, ["precision", "number"])
        ], 2))
      : _createCommentVNode("", true),
    (!_unref(isXS))
      ? (_openBlock(), _createElementBlock("td", _hoisted_8, [
          _createVNode(_component_ot_volume_display, { number: fiatVolume.value }, null, 8, ["number"])
        ]))
      : _createCommentVNode("", true),
    (_unref(isXS))
      ? (_openBlock(), _createElementBlock("td", _hoisted_9, [
          _createElementVNode("div", _hoisted_10, [
            (__props.market.allDay.pricePointData.length && __props.withPriceChangeChart)
              ? (_openBlock(), _createBlock(MiniatureLineChart, {
                  key: 0,
                  "chart-data": __props.market.allDay.pricePointData,
                  "chart-style": { height: '20px', width: '70px' },
                  "line-color": colorVal.value
                }, null, 8, ["chart-data", "line-color"]))
              : _createCommentVNode("", true),
            _createElementVNode("div", _hoisted_11, [
              (isNewMarket.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                    _createVNode(_component_ot_lozenge, {
                      uppercase: true,
                      text: _ctx.$t('modules.marketList.new')
                    }, null, 8, ["text"])
                  ]))
                : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                    _createVNode(_component_ot_number_display, {
                      class: _normalizeClass(["market-list-item__price", animatePriceChangingClass.value]),
                      precision: __props.market.marketPrecision,
                      number: __props.market.lastPrice
                    }, null, 8, ["class", "precision", "number"]),
                    _createVNode(_component_ot_number_display, {
                      class: _normalizeClass(priceChangeClass.value),
                      precision: defaultPrecision.percentage,
                      number: __props.market.priceChangePercentageToDisplay,
                      "add-plus-sign": true,
                      percentage: true
                    }, null, 8, ["class", "precision", "number"])
                  ], 64))
            ])
          ])
        ]))
      : (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
          (!isNewMarket.value)
            ? (_openBlock(), _createElementBlock("td", _hoisted_13, [
                _createVNode(_component_ot_number_display, {
                  class: _normalizeClass(priceChangeClass.value),
                  precision: defaultPrecision.percentage,
                  number: __props.market.priceChangePercentageToDisplay,
                  "add-plus-sign": true,
                  percentage: true
                }, null, 8, ["class", "precision", "number"])
              ]))
            : (_openBlock(), _createElementBlock("td", _hoisted_14, [
                _createVNode(_component_ot_lozenge, {
                  uppercase: true,
                  text: _ctx.$t('modules.marketList.new')
                }, null, 8, ["text"])
              ]))
        ], 64))
  ], 2))
}
}

})